import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { format, subDays } from "date-fns";
import {
  Info,
  Lock,
  CalendarRange,
  ExternalLink,
  ChevronDown,
  ChevronRight,
  Activity,
  Users,
  Shield,
} from "lucide-react";
import { Link } from "react-router-dom";

import { DataTable } from "@/components/admin/DataTable";
import {
  getAuditLogs,
  type AuditLogFilters,
} from "@/lib/admin/queries";

import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import type { AuditLog } from "@/types/database";

// ─── Types ────────────────────────────────────────────────────────────────────

type AuditLogRow = AuditLog & {
  actor?: {
    id: string;
    full_name: string;
    email: string;
    role: string;
  } | null;
};

// ─── Constants ────────────────────────────────────────────────────────────────

const ACTION_TYPES = [
  "created",
  "updated",
  "deleted",
  "approved",
  "denied",
  "login",
  "export",
  "override",
  "cancelled",
  "suspended",
  "resolved",
];

const ENTITY_TYPES = [
  "user",
  "building",
  "unit",
  "booking",
  "rule",
  "payment",
  "profile",
  "organization",
  "host",
  "strata_config",
  "subscription",
  "announcement",
  "message",
  "platform",
];

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatAction(action: string): string {
  return action.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

function actionBadgeColor(action: string): string {
  const a = action.toLowerCase();
  if (a.includes("approv") || a.includes("creat") || a.includes("add"))
    return "bg-green-100 text-green-700 border-green-200";
  if (a.includes("deni") || a.includes("delet") || a.includes("remov") || a.includes("suspend"))
    return "bg-red-100 text-red-700 border-red-200";
  if (a.includes("updat") || a.includes("edit") || a.includes("override") || a.includes("reset"))
    return "bg-amber-100 text-amber-700 border-amber-200";
  if (a.includes("login") || a.includes("sign"))
    return "bg-blue-100 text-blue-700 border-blue-200";
  if (a.includes("export"))
    return "bg-purple-100 text-purple-700 border-purple-200";
  return "bg-gray-100 text-gray-600 border-gray-200";
}

function entityLink(entityType: string, entityId: string): string | null {
  switch (entityType.toLowerCase()) {
    case "user":
    case "profile":
      return `/admin/users/${entityId}`;
    case "organization":
    case "building":
      return `/admin/buildings/${entityId}`;
    case "unit":
      return `/admin/units/${entityId}`;
    case "booking":
      return `/admin/bookings/${entityId}`;
    default:
      return null;
  }
}

function tryParseJson(val: string | null | undefined): Record<string, unknown> | null {
  if (!val) return null;
  try {
    const parsed = JSON.parse(val);
    if (typeof parsed === "object" && parsed !== null) return parsed as Record<string, unknown>;
  } catch {
    /* empty */
  }
  return null;
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function JsonDiff({ data }: { data: Record<string, unknown> }) {
  const before = data.before as Record<string, unknown> | undefined;
  const after = data.after as Record<string, unknown> | undefined;

  if (!before && !after) {
    return (
      <pre className="text-xs bg-gray-950 text-green-400 p-3 rounded overflow-x-auto whitespace-pre-wrap break-all">
        {JSON.stringify(data, null, 2)}
      </pre>
    );
  }

  const keys = new Set([
    ...Object.keys(before ?? {}),
    ...Object.keys(after ?? {}),
  ]);

  return (
    <div className="space-y-1">
      {Array.from(keys).map((key) => {
        const bVal = JSON.stringify(before?.[key]);
        const aVal = JSON.stringify(after?.[key]);
        const changed = bVal !== aVal;
        return (
          <div
            key={key}
            className={`flex gap-2 rounded px-2 py-1 text-xs font-mono ${changed ? "bg-amber-50" : ""}`}
          >
            <span className="text-gray-400 shrink-0 w-36 truncate">{key}</span>
            {changed ? (
              <div className="flex flex-col gap-0.5">
                <span className="text-red-600 line-through">{bVal ?? "null"}</span>
                <span className="text-green-700">{aVal ?? "null"}</span>
              </div>
            ) : (
              <span className="text-gray-600">{aVal ?? "null"}</span>
            )}
          </div>
        );
      })}
    </div>
  );
}

function LogDetailSheet({
  log,
  open,
  onClose,
}: {
  log: AuditLogRow | null;
  open: boolean;
  onClose: () => void;
}) {
  const parsed = log ? tryParseJson(log.detail) : null;
  const link = log
    ? entityLink(log.entity, parsed?.entityId as string ?? log.entity)
    : null;

  return (
    <Sheet open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <SheetContent className="sm:max-w-lg overflow-y-auto">
        <SheetHeader>
          <SheetTitle>Audit Log Entry</SheetTitle>
          <SheetDescription>
            Full details for log ID{" "}
            <span className="font-mono text-xs">{log?.id?.slice(0, 8)}</span>
          </SheetDescription>
        </SheetHeader>

        {log && (
          <div className="mt-6 space-y-4">
            {/* Meta */}
            <div className="space-y-2">
              <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-2 text-sm">
                <dt className="text-xs font-semibold text-gray-400 uppercase tracking-wide">Timestamp</dt>
                <dd className="text-gray-900 tabular-nums">
                  {format(new Date(log.created_at), "PPpp")}
                </dd>
                <dt className="text-xs font-semibold text-gray-400 uppercase tracking-wide">Actor</dt>
                <dd className="text-gray-900 font-medium">
                  {log.actor?.full_name ?? log.user_name}
                  {log.actor?.email && (
                    <span className="text-gray-400 font-normal ml-2 text-xs">
                      ({log.actor.email})
                    </span>
                  )}
                </dd>
                <dt className="text-xs font-semibold text-gray-400 uppercase tracking-wide">Role</dt>
                <dd>
                  <span className="text-xs bg-gray-100 px-2 py-0.5 rounded-full text-gray-600 font-medium">
                    {log.actor?.role ?? "—"}
                  </span>
                </dd>
                <dt className="text-xs font-semibold text-gray-400 uppercase tracking-wide">Action</dt>
                <dd>
                  <span
                    className={`text-xs px-2 py-0.5 rounded border font-medium ${actionBadgeColor(log.action)}`}
                  >
                    {formatAction(log.action)}
                  </span>
                </dd>
                <dt className="text-xs font-semibold text-gray-400 uppercase tracking-wide">Entity Type</dt>
                <dd className="text-gray-900">{log.entity}</dd>
              </dl>
            </div>

            {/* Related entity link */}
            {link && (
              <>
                <Separator />
                <div>
                  <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">
                    Related Entity
                  </p>
                  <Link
                    to={link}
                    onClick={onClose}
                    className="inline-flex items-center gap-1.5 text-sm text-blue-600 hover:text-blue-700 font-medium"
                  >
                    <ExternalLink className="h-3.5 w-3.5" />
                    View {log.entity}
                  </Link>
                </div>
              </>
            )}

            {/* Diff */}
            {parsed && (
              <>
                <Separator />
                <div>
                  <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">
                    Details / Diff
                  </p>
                  <JsonDiff data={parsed} />
                </div>
              </>
            )}
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}

// ─── Component ────────────────────────────────────────────────────────────────

export default function AuditLogsPage() {
  // Filters
  const [search, setSearch] = useState("");
  const [actionFilter, setActionFilter] = useState("all");
  const [entityFilter, setEntityFilter] = useState("all");
  const [dateFrom, setDateFrom] = useState(format(subDays(new Date(), 30), "yyyy-MM-dd"));
  const [dateTo, setDateTo] = useState(format(new Date(), "yyyy-MM-dd"));
  const [ipSearch, setIpSearch] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  // Row expansion
  const [selectedLog, setSelectedLog] = useState<AuditLogRow | null>(null);
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());

  // ─── Query ────────────────────────────────────────────────────────────────

  const filters: AuditLogFilters = {
    ...(search ? { search } : {}),
    ...(actionFilter !== "all" ? { action: actionFilter } : {}),
    ...(entityFilter !== "all" ? { entityType: entityFilter } : {}),
    ...(dateFrom ? { dateFrom: new Date(dateFrom).toISOString() } : {}),
    ...(dateTo ? { dateTo: new Date(dateTo + "T23:59:59").toISOString() } : {}),
  };

  const { data: logsResult, isLoading } = useQuery({
    queryKey: ["admin", "audit-logs", filters, page, pageSize],
    queryFn: () => getAuditLogs(filters, page, pageSize),
  });

  const logs = (logsResult?.data ?? []) as AuditLogRow[];
  const totalCount = logsResult?.count ?? 0;

  // ─── 7-day rolling stats ──────────────────────────────────────────────────

  const sevenDayStats = useQuery({
    queryKey: ["admin", "audit-logs-stats"],
    queryFn: () =>
      getAuditLogs(
        {
          dateFrom: subDays(new Date(), 7).toISOString(),
          dateTo: new Date().toISOString(),
        },
        1,
        200
      ),
  });

  const statsLogs = (sevenDayStats.data?.data ?? []) as AuditLogRow[];
  const totalActions7d = statsLogs.length;

  const adminCounts = statsLogs.reduce<Record<string, number>>((acc, l) => {
    const name = l.actor?.full_name ?? l.user_name ?? "Unknown";
    acc[name] = (acc[name] ?? 0) + 1;
    return acc;
  }, {});
  const mostActiveAdmin = Object.entries(adminCounts).sort((a, b) => b[1] - a[1])[0]?.[0] ?? "—";

  const actionCounts = statsLogs.reduce<Record<string, number>>((acc, l) => {
    const a = l.action ?? "unknown";
    acc[a] = (acc[a] ?? 0) + 1;
    return acc;
  }, {});
  const mostCommonAction = Object.entries(actionCounts).sort((a, b) => b[1] - a[1])[0]?.[0] ?? "—";

  // ─── Export ───────────────────────────────────────────────────────────────

  function handleExport() {
    const rows = logs.map((l) => ({
      timestamp: l.created_at,
      actor: l.actor?.full_name ?? l.user_name,
      role: l.actor?.role ?? "",
      action: l.action,
      entity_type: l.entity,
      details: l.detail?.replace(/,/g, ";") ?? "",
    }));
    const header = Object.keys(rows[0] ?? {}).join(",");
    const csv = [header, ...rows.map((r) => Object.values(r).map((v) => `"${v}"`).join(","))].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `audit-logs-${format(new Date(), "yyyy-MM-dd")}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  // ─── Toggle row expansion ─────────────────────────────────────────────────

  function toggleRow(id: string) {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  // ─── Columns ──────────────────────────────────────────────────────────────

  const columns = [
    {
      key: "created_at",
      label: "Timestamp",
      sortable: true,
      render: (row: Record<string, unknown>) => {
        const log = row as unknown as AuditLogRow;
        return (
          <button
            className="flex items-center gap-1.5 group text-left"
            onClick={() => toggleRow(log.id)}
            aria-expanded={expandedRows.has(log.id)}
            aria-label="Expand row details"
          >
            {expandedRows.has(log.id) ? (
              <ChevronDown className="h-3.5 w-3.5 text-gray-400 shrink-0" />
            ) : (
              <ChevronRight className="h-3.5 w-3.5 text-gray-300 group-hover:text-gray-400 shrink-0" />
            )}
            <span className="text-xs tabular-nums text-gray-500 whitespace-nowrap">
              {format(new Date(log.created_at), "MMM d, yyyy HH:mm:ss")}
            </span>
          </button>
        );
      },
    },
    {
      key: "actor",
      label: "Actor",
      render: (row: Record<string, unknown>) => {
        const log = row as unknown as AuditLogRow;
        return (
          <div className="flex flex-col">
            <span className="text-sm font-medium text-gray-900 truncate max-w-[140px]">
              {log.actor?.full_name ?? log.user_name}
            </span>
            {log.actor?.email && (
              <span className="text-xs text-gray-400 truncate max-w-[140px]">{log.actor.email}</span>
            )}
          </div>
        );
      },
    },
    {
      key: "role",
      label: "Role",
      render: (row: Record<string, unknown>) => {
        const log = row as unknown as AuditLogRow;
        return (
          <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full font-medium whitespace-nowrap">
            {log.actor?.role ?? "—"}
          </span>
        );
      },
    },
    {
      key: "action",
      label: "Action",
      sortable: true,
      render: (row: Record<string, unknown>) => {
        const log = row as unknown as AuditLogRow;
        return (
          <span
            className={`inline-flex text-xs px-2 py-0.5 rounded border font-medium whitespace-nowrap ${actionBadgeColor(log.action)}`}
          >
            {formatAction(log.action)}
          </span>
        );
      },
    },
    {
      key: "entity",
      label: "Entity Type",
      render: (row: Record<string, unknown>) => {
        const log = row as unknown as AuditLogRow;
        return (
          <span className="text-sm text-gray-600 capitalize whitespace-nowrap">
            {log.entity?.replace(/_/g, " ")}
          </span>
        );
      },
    },
    {
      key: "detail",
      label: "Details",
      render: (row: Record<string, unknown>) => {
        const log = row as unknown as AuditLogRow;
        const parsed = tryParseJson(log.detail);
        const entityId = parsed?.entityId as string | undefined;
        return (
          <span className="font-mono text-xs text-gray-400 truncate max-w-[180px] block">
            {entityId ? entityId.slice(0, 12) + "…" : log.detail?.slice(0, 40) ?? "—"}
          </span>
        );
      },
    },
    {
      key: "expand",
      label: "",
      render: (row: Record<string, unknown>) => {
        const log = row as unknown as AuditLogRow;
        return (
          <Button
            variant="ghost"
            size="sm"
            className="h-7 text-xs text-blue-600 hover:text-blue-700 whitespace-nowrap"
            onClick={() => setSelectedLog(log)}
          >
            View
          </Button>
        );
      },
    },
  ];

  // ─── Filter UI ────────────────────────────────────────────────────────────

  const filtersNode = (
    <>
      <Select
        value={actionFilter}
        onValueChange={(v) => { setActionFilter(v); setPage(1); }}
      >
        <SelectTrigger className="h-9 w-[150px] text-sm">
          <SelectValue placeholder="Action type" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">All actions</SelectItem>
          {ACTION_TYPES.map((a) => (
            <SelectItem key={a} value={a}>
              {a.replace(/\b\w/g, (c) => c.toUpperCase())}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <Select
        value={entityFilter}
        onValueChange={(v) => { setEntityFilter(v); setPage(1); }}
      >
        <SelectTrigger className="h-9 w-[150px] text-sm">
          <SelectValue placeholder="Entity type" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">All entities</SelectItem>
          {ENTITY_TYPES.map((e) => (
            <SelectItem key={e} value={e}>
              {e.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <div className="flex items-center gap-1.5">
        <CalendarRange className="h-4 w-4 text-gray-400 shrink-0" />
        <Input
          type="date"
          value={dateFrom}
          onChange={(e) => { setDateFrom(e.target.value); setPage(1); }}
          className="h-9 w-[130px] text-sm"
          aria-label="Date from"
        />
        <span className="text-gray-400 text-sm">—</span>
        <Input
          type="date"
          value={dateTo}
          onChange={(e) => { setDateTo(e.target.value); setPage(1); }}
          className="h-9 w-[130px] text-sm"
          aria-label="Date to"
        />
      </div>

      <Input
        placeholder="IP address…"
        value={ipSearch}
        onChange={(e) => { setIpSearch(e.target.value); setPage(1); }}
        className="h-9 w-[140px] text-sm"
        aria-label="IP address search"
      />
    </>
  );

  // ─── Render ───────────────────────────────────────────────────────────────

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">Audit Logs</h1>
        <p className="text-xs text-gray-400">Showing logs from the last 365 days</p>
      </div>

      {/* Immutability banner */}
      <div className="flex items-start gap-3 rounded-md bg-blue-50 border border-blue-200 px-4 py-3">
        <Lock className="h-4 w-4 text-blue-600 shrink-0 mt-0.5" />
        <p className="text-sm text-blue-700">
          <strong>Audit logs are immutable.</strong> Records cannot be modified or deleted. All
          admin actions are automatically logged for compliance and accountability.
        </p>
      </div>

      {/* 7-day rolling stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-500 flex items-center gap-2 whitespace-nowrap">
              <Activity className="h-4 w-4" />
              Total Actions (7d)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-3xl font-bold text-gray-900 tabular-nums">
              {sevenDayStats.isLoading ? "—" : totalActions7d}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-500 flex items-center gap-2 whitespace-nowrap">
              <Users className="h-4 w-4" />
              Most Active Admin (7d)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-base font-semibold text-gray-900 truncate">
              {sevenDayStats.isLoading ? "—" : mostActiveAdmin}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-500 flex items-center gap-2 whitespace-nowrap">
              <Shield className="h-4 w-4" />
              Most Common Action (7d)
            </CardTitle>
          </CardHeader>
          <CardContent>
            {sevenDayStats.isLoading ? (
              <p className="text-base font-semibold text-gray-900">—</p>
            ) : (
              <span
                className={`text-sm px-2 py-0.5 rounded border font-medium ${actionBadgeColor(mostCommonAction)}`}
              >
                {formatAction(mostCommonAction)}
              </span>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Table */}
      <DataTable
        columns={columns}
        data={logs as unknown as Record<string, unknown>[]}
        loading={isLoading}
        totalCount={totalCount}
        page={page}
        pageSize={pageSize}
        onPageChange={setPage}
        onPageSizeChange={(s) => { setPageSize(s); setPage(1); }}
        onSearch={(q) => { setSearch(q); setPage(1); }}
        searchPlaceholder="Search actor name, email or action…"
        onExport={handleExport}
        filters={filtersNode}
        emptyMessage="No audit log entries found for the selected filters."
      />

      {/* Detail Sheet */}
      <LogDetailSheet
        log={selectedLog}
        open={!!selectedLog}
        onClose={() => setSelectedLog(null)}
      />
    </div>
  );
}
